/**
 * Durability (D152) — a thing that can be spent, and what a spent thing
 * refuses.
 *
 * @remarks
 * Two seams in the turn pipeline, both one interception for EVERY act so a
 * verb added next year is covered without anyone remembering this — the
 * reach gate's shape (R29):
 *
 * - **Before the handler**, the spent gate: an act a spent thing refuses is
 *   rejected in the item's own words (`spentRefusalNarratives`) or the
 *   `itemSpent` slot, and no turn passes.
 * - **After the handler**, the spend: an act in `spentBy` that COMPLETED
 *   counts one use; the act that crosses zero speaks `spentNarratives` and
 *   applies `onSpent`, once. A refused act spends nothing — the strap that
 *   would not come off was not pulled.
 *
 * The count lives in `world.spentUses`; whether the checkpoint rewinds it is
 * the item's `durability.scope` (Mike, 2026-09-07: a pack declaration).
 *
 * @packageDocumentation
 */

import { type Durability, type ItemAct, itemActSchema, type ItemId, refusedActsOf, type RuntimeState } from '@orpheus/content-schema';

import type { RuntimeAction } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireItem } from '../bundle/bundle-lookups.js';
import { speakSlot } from '../defaults/default-responses.js';
import { isItemSpent } from '../entities/item-state.js';
import { type ActionHandlerResult, type CompletedAction, rejected } from './action-handler-result.js';
import { stampNarrativeLines } from './transcript-cursor.js';
import { withEffects } from './with-effects.js';

// The pack's act vocabulary is a subset of the engine's actions by
// construction — a new `ItemAct` that names no action fails to compile here.
itemActSchema.options satisfies readonly RuntimeAction['type'][];

/** The act an action performs on an item, and which item — `use` acts with its INSTRUMENT. */
const actedItemOf = (action: RuntimeAction): { readonly act: ItemAct; readonly itemId: ItemId } | undefined => {
	switch (action.type) {
		case 'close_container':
		case 'drop':
		case 'equip':
		case 'inspect':
		case 'open_container':
		case 'search':
		case 'switch_off':
		case 'switch_on':
		case 'take':
		case 'unequip': {
			return { act: action.type, itemId: action.itemId };
		}
		case 'use': {
			return { act: 'use', itemId: action.instrumentItemId };
		}
		default: {
			return undefined;
		}
	}
};

/** How many times the thing has been spent. */
const spentCountOf = (state: RuntimeState, itemId: ItemId): number => state.world.spentUses[itemId] ?? 0;

/**
 * The spent gate: the rejection for an act a spent thing refuses, if this
 * action is one.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param action - The narrowed action
 * @returns The rejection, or `undefined` when the act may proceed
 */
export const findSpentRefusal = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: RuntimeAction,
): Extract<ActionHandlerResult, { type: 'rejected' }> | undefined => {
	const acted = actedItemOf(action);

	if (acted === undefined) {
		return undefined;
	}

	const item = requireItem(bundle, acted.itemId);
	const { durability } = item;

	if (durability === undefined || !refusedActsOf(durability).includes(acted.act) || !isItemSpent(item, state)) {
		return undefined;
	}

	return rejected(
		bundle,
		state,
		{ itemId: acted.itemId, type: 'item_spent' },
		durability.spentRefusalNarratives ?? [speakSlot(bundle, state, 'itemSpent', { itemName: item.name })],
	);
};

/**
 * The spend: count a completed act in `spentBy`, and on the crossing speak
 * the thing's last line and apply what happens to it.
 *
 * @param bundle - The loaded bundle
 * @param action - The action that completed
 * @param handled - The handler's completed result
 * @returns The result with the spend folded in
 */
export const spendDurability = (bundle: EngineGameBundle, action: RuntimeAction, handled: CompletedAction): CompletedAction => {
	const acted = actedItemOf(action);

	if (acted === undefined) {
		return handled;
	}

	const { durability } = requireItem(bundle, acted.itemId);

	if (durability === undefined || !durability.spentBy.includes(acted.act)) {
		return handled;
	}

	const count = spentCountOf(handled.state, acted.itemId) + 1;
	const counted: CompletedAction = {
		...handled,
		state: { ...handled.state, world: { ...handled.state.world, spentUses: { ...handled.state.world.spentUses, [acted.itemId]: count }}},
	};

	// The crossing fires ONCE, on the act that reaches the declared uses; a
	// spent thing spent again (an act it does not refuse) just counts.
	return count === durability.uses ? crossZero(bundle, counted, acted.itemId, durability) : counted;
};

/** The crossing: the authored last line, then what happens to the thing. */
const crossZero = (bundle: EngineGameBundle, counted: CompletedAction, itemId: ItemId, durability: Durability): CompletedAction => {
	const stamped = stampNarrativeLines(bundle, counted.state, counted.cursor, durability.spentNarratives ?? [], `spent:${itemId}`);
	const spoken: CompletedAction = { ...counted, cursor: stamped.cursor, entries: [...counted.entries, ...stamped.entries]};

	return withEffects(bundle, spoken, durability.onSpent);
};
